import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Lock, CheckCircle, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [token, setToken] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setError('Invalid reset link. Please request a new password reset.');
    } else {
      setToken(tokenParam);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        token: token,
        new_password: newPassword
      });

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (err) {
      if (err.response?.status === 400) {
        setError('This reset link is invalid or has expired. Please request a new one.');
      } else if (err.response?.status === 429) {
        setError('Too many requests. Please try again later.');
      } else {
        setError(err.response?.data?.detail || 'Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token && !error) {
    return (
      <div className="app-content bg-[#050505] min-h-screen flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="app-content bg-[#050505] min-h-screen">
      <Navbar />
      
      <section className="py-32">
        <div className="max-w-md mx-auto px-6">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl md:text-5xl font-black uppercase mb-2">
              <span className="text-red-500">RESET</span> PASSWORD
            </h1>
            <p className="text-gray-400 font-['Rajdhani'] text-lg">
              Enter your new password
            </p>
          </div>

          {/* Success Message */}
          {success ? (
            <div className="bg-[#0A0A0A] border border-green-500/50 p-8">
              <div className="text-center">
                <div className="mb-4">
                  <CheckCircle className="mx-auto text-green-500" size={48} />
                </div>
                <h2 className="text-2xl font-bold text-white mb-4">PASSWORD RESET SUCCESSFUL!</h2>
                <p className="text-gray-400 mb-6">
                  Your password has been reset successfully.
                </p>
                <p className="text-sm text-gray-500">
                  Redirecting to login page...
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-[#0A0A0A] border border-white/10 p-8">
              {error && (
                <div className="mb-6 p-4 bg-red-900/20 border border-red-500/50 flex items-start gap-3">
                  <XCircle className="text-red-500 flex-shrink-0" size={20} />
                  <p className="text-red-500 text-sm">{error}</p>
                </div>
              )}

              <div className="mb-6">
                <label className="text-sm uppercase tracking-wider text-gray-400 mb-2 block">
                  NEW PASSWORD
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500" size={20} />
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    placeholder="Enter new password"
                    minLength="6"
                    className="w-full bg-black border border-white/10 focus:border-red-500 text-white h-12 pl-12 pr-4 font-['Rajdhani'] transition-all"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">At least 6 characters</p>
              </div>

              <div className="mb-6">
                <label className="text-sm uppercase tracking-wider text-gray-400 mb-2 block">
                  CONFIRM PASSWORD
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500" size={20} />
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    placeholder="Confirm new password"
                    minLength="6"
                    className="w-full bg-black border border-white/10 focus:border-red-500 text-white h-12 pl-12 pr-4 font-['Rajdhani'] transition-all"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !token}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-black py-4 uppercase tracking-wider transition-all disabled:opacity-50 disabled:cursor-not-allowed mb-4"
              >
                {loading ? 'RESETTING...' : 'RESET PASSWORD'}
              </button>

              <div className="text-center">
                <Link 
                  to="/login"
                  className="text-gray-400 hover:text-red-500 text-sm transition-colors"
                >
                  Back to Login
                </Link>
              </div>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ResetPasswordPage;